/**
 * Drawer flottant à 2 onglets :
 *  - « En ligne »       : liste des utilisateurs présents (formateurs, étudiants, agents, parents)
 *  - « Conversations »  : historique complet des conversations de l'utilisateur
 *
 * Toggle de visibilité personnelle en tête. Clic sur une entrée →
 * ouvre une conversation directe (via onOpenConversation).
 */
import React, { useMemo, useState } from 'react';
import {
  X, Users, MessageSquare, Eye, EyeOff, Search, Wifi, Inbox,
  GraduationCap, LogIn as LogInIcon,
} from 'lucide-react';
import {
  useListerPresenceQuery,
  useOuvrirDirectMutation,
  useChangerVisibiliteMutation,
  useListerConversationsQuery,
  useMaClasseGroupeQuery,
  useRejoindreGroupeClasseMutation,
  PresenceItem,
  ConversationItem,
} from '../../services/messagerieChat';

interface Props {
  open: boolean;
  onClose: () => void;
  isDark?: boolean;
  /** Callback quand l'utilisateur ouvre une conversation. Reçoit conv_id. */
  onOpenConversation?: (convId: number, user: PresenceItem) => void;
  /** Fallback lien vers messagerie backoffice si onOpenConversation non fourni */
  fallbackHref?: string;
}

const ROLE_COLORS: Record<string, string> = {
  formateur: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  etudiant: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  parent: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  agent: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  admin: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  super_admin: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  surveillant: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
};

const ROLE_ORDER = ['super_admin', 'admin', 'agent', 'formateur', 'surveillant', 'etudiant', 'parent'];

// Groupement en catégories UX pour l'affichage de la liste
type CategorieRole = 'personnel' | 'formateur' | 'surveillant' | 'etudiant' | 'parent' | 'autres';
const CATEGORIE_LABELS: Record<CategorieRole, string> = {
  personnel: 'Personnel administratif',
  formateur: 'Formateurs',
  surveillant: 'Surveillants',
  etudiant: 'Étudiants',
  parent: 'Parents',
  autres: 'Autres',
};
const CATEGORIE_ORDER: CategorieRole[] = ['personnel', 'formateur', 'surveillant', 'etudiant', 'parent', 'autres'];

function categorieOf(role: string): CategorieRole {
  if (role === 'super_admin' || role === 'admin' || role === 'agent' || role === 'agent_caisse'
      || role === 'directeur' || role === 'invite') return 'personnel';
  if (role === 'formateur') return 'formateur';
  if (role === 'surveillant') return 'surveillant';
  if (role === 'etudiant') return 'etudiant';
  if (role === 'parent') return 'parent';
  return 'autres';
}

type TabKey = 'en_ligne' | 'convs';

function fmtRelatif(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMin = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.round(h / 24);
  if (j === 1) return 'hier';
  if (j < 7) return `il y a ${j} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

export const OnlineUsersDrawer: React.FC<Props> = ({
  open, onClose, isDark = false, onOpenConversation, fallbackHref,
}) => {
  const [tab, setTab] = useState<TabKey>('en_ligne');
  const { data: presence, refetch: refetchPres, isFetching: fetchPres } = useListerPresenceQuery(undefined, {
    skip: !open,
    pollingInterval: open ? 30000 : 0,
  });
  const { data: convs, refetch: refetchConvs, isFetching: fetchConvs } = useListerConversationsQuery(undefined, {
    skip: !open,
    pollingInterval: open ? 15000 : 0,
  });
  const [ouvrirDirect, { isLoading: opening }] = useOuvrirDirectMutation();
  const [changerVisibilite] = useChangerVisibiliteMutation();

  // ── Groupe de classe (visible uniquement pour les étudiants portail) ──
  const isEtudiantPortail = typeof window !== 'undefined'
    && !!localStorage.getItem('portail_access_token')
    && !localStorage.getItem('auth_token')
    && !localStorage.getItem('access_token');
  const { data: groupeClasseInfo, refetch: refetchGroupe } = useMaClasseGroupeQuery(undefined, {
    skip: !open || !isEtudiantPortail,
  });
  const [rejoindreClasse, { isLoading: joiningClasse }] = useRejoindreGroupeClasseMutation();

  const handleRejoindreClasse = async () => {
    try {
      const r = await rejoindreClasse().unwrap();
      refetchGroupe();
      if (onOpenConversation) {
        // Utiliser un PresenceItem factice pour ouvrir la conversation
        const fake: PresenceItem = {
          user_id: 0, user_nom: r.libelle,
          user_role: '', role_label: '',
          statut: 'offline', page_courante: '', activite: '', dernier_ping: null,
        };
        onOpenConversation(r.conv_id, fake);
      }
    } catch (e: any) {
      alert(e?.data?.detail || 'Impossible de rejoindre le groupe');
    }
  };

  const handleOuvrirGroupeClasse = () => {
    if (!groupeClasseInfo?.conv_id) return;
    if (onOpenConversation) {
      const fake: PresenceItem = {
        user_id: 0, user_nom: groupeClasseInfo.libelle || 'Groupe classe',
        user_role: '', role_label: '',
        statut: 'offline', page_courante: '', activite: '', dernier_ping: null,
      };
      onOpenConversation(groupeClasseInfo.conv_id, fake);
    }
  };

  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState<CategorieRole | ''>('');

  // ── EN LIGNE
  const usersFiltered = useMemo(() => {
    let list = presence?.results || [];
    if (filterCat) list = list.filter(u => categorieOf(u.user_role) === filterCat);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(u => u.user_nom.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      const ra = ROLE_ORDER.indexOf(a.user_role);
      const rb = ROLE_ORDER.indexOf(b.user_role);
      const aOrder = ra === -1 ? 999 : ra;
      const bOrder = rb === -1 ? 999 : rb;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.user_nom.localeCompare(b.user_nom, 'fr');
    });
  }, [presence, filterCat, search]);

  // Groupe les users filtrés par catégorie pour l'affichage en sections
  const usersParCategorie = useMemo(() => {
    const map: Record<CategorieRole, PresenceItem[]> = {
      personnel: [], formateur: [], surveillant: [], etudiant: [], parent: [], autres: [],
    };
    usersFiltered.forEach(u => { map[categorieOf(u.user_role)].push(u); });
    return map;
  }, [usersFiltered]);

  const catsPresentes = useMemo(() => {
    const set = new Set<CategorieRole>();
    (presence?.results || []).forEach(u => set.add(categorieOf(u.user_role)));
    return CATEGORIE_ORDER.filter(c => set.has(c));
  }, [presence]);

  // Compteurs par catégorie (avant filtre) pour les chips
  const countsParCategorie = useMemo(() => {
    const map: Record<CategorieRole, number> = {
      personnel: 0, formateur: 0, surveillant: 0, etudiant: 0, parent: 0, autres: 0,
    };
    (presence?.results || []).forEach(u => { map[categorieOf(u.user_role)]++; });
    return map;
  }, [presence]);

  // ── CONVERSATIONS
  const convsFiltered = useMemo(() => {
    let list = convs?.results || [];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(c => c.titre_affichage.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) =>
      new Date(b.date_derniere_activite).getTime() - new Date(a.date_derniere_activite).getTime(),
    );
  }, [convs, search]);

  const totalConnected = presence?.total ?? 0;
  const totalConvs = convs?.results?.length ?? 0;
  const totalNonLus = (convs?.results || []).reduce((s, c) => s + (c.nb_non_lus || 0), 0);

  const openChat = async (u: PresenceItem) => {
    try {
      const conv = await ouvrirDirect({ user_id: u.user_id }).unwrap();
      if (onOpenConversation) {
        onOpenConversation(conv.id, u);
      } else if (fallbackHref) {
        window.location.href = `${fallbackHref}?conv=${conv.id}`;
      }
    } catch (e: any) {
      alert(e?.data?.detail || 'Impossible d\'ouvrir la conversation');
    }
  };

  const openConv = (c: ConversationItem) => {
    if (onOpenConversation) {
      // Fabriquer un pseudo PresenceItem pour compatibilité de signature
      const first = c.participants_apercu?.[0];
      const fake: PresenceItem = {
        user_id: first?.user_id ?? 0,
        user_nom: c.titre_affichage,
        user_role: first?.role_conv || '',
        role_label: '',
        statut: 'offline',
        page_courante: '',
        activite: '',
        dernier_ping: null,
      };
      onOpenConversation(c.id, fake);
    } else if (fallbackHref) {
      window.location.href = `${fallbackHref}?conv=${c.id}`;
    }
  };

  const toggleMyVisibility = async () => {
    const current = presence?.visible_annuaire ?? true;
    // Confirmation avant de se masquer : les collègues ne vous verront plus
    // dans la liste des connectés (mais ils pourront toujours vous écrire
    // via une conversation existante). Évite les clics accidentels.
    if (current) {
      const ok = window.confirm(
        "En vous masquant, vos collègues ne vous verront plus dans la liste des connectés. "
        + "Ils pourront toujours vous écrire via une conversation existante.\n\n"
        + "Confirmer le masquage ?"
      );
      if (!ok) return;
    }
    try {
      await changerVisibilite({ visible: !current }).unwrap();
      refetchPres();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  if (!open) return null;

  const refresh = () => {
    if (tab === 'en_ligne') refetchPres();
    else refetchConvs();
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-96 shadow-2xl flex flex-col ${
          isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
        }`}
      >
        <header className={`px-4 py-3 border-b flex items-center justify-between ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MessageSquare size={20} className="text-blue-500" />
              {totalNonLus > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {totalNonLus > 99 ? '99+' : totalNonLus}
                </span>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-sm">Messagerie</h3>
              <p className="text-[11px] text-gray-500">
                {totalConnected} en ligne · {totalConvs} conversation{totalConvs > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </header>

        {/* Onglets */}
        <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setTab('en_ligne')}
            className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
              tab === 'en_ligne'
                ? 'border-blue-500 text-blue-500'
                : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
            }`}
          >
            <Users size={14} />
            En ligne
            {totalConnected > 0 && (
              <span className={`ml-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${
                tab === 'en_ligne' ? 'bg-blue-500 text-white' : (isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600')
              }`}>{totalConnected}</span>
            )}
          </button>
          <button
            onClick={() => setTab('convs')}
            className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
              tab === 'convs'
                ? 'border-blue-500 text-blue-500'
                : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
            }`}
          >
            <MessageSquare size={14} />
            Conversations
            {totalNonLus > 0 && (
              <span className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center bg-red-500 text-white">
                {totalNonLus}
              </span>
            )}
          </button>
        </div>

        {/* Hint onboarding : rassure explicitement les étudiants
            qu'ils peuvent écrire à un enseignant / agent. Détection auto
            via présence du token portail étudiant en localStorage. */}
        {tab === 'en_ligne' && isEtudiantPortail && (
          <div className={`px-4 py-2.5 border-b text-[11px] leading-snug ${
            isDark ? 'bg-blue-950/30 border-blue-900/50 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <strong>Astuce :</strong> tous les <em>formateurs</em>, <em>agents de la scolarité</em> et <em>camarades</em> en ligne apparaissent ci-dessous.
            Tapez sur une personne pour lui écrire directement.
          </div>
        )}

        {/* Groupe de classe (étudiants uniquement) — bouton rejoindre ou ouvrir */}
        {isEtudiantPortail && groupeClasseInfo?.eligible && groupeClasseInfo.classe && (
          <div className={`px-4 py-2.5 border-b ${
            isDark ? 'bg-emerald-950/20 border-emerald-900/50' : 'bg-emerald-50 border-emerald-200'
          }`}>
            <div className="flex items-start gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isDark ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
              }`}>
                <GraduationCap size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[13px] font-semibold ${isDark ? 'text-emerald-200' : 'text-emerald-800'}`}>
                  {groupeClasseInfo.libelle || `Groupe ${groupeClasseInfo.classe}`}
                </div>
                <div className={`text-[10px] mt-0.5 ${isDark ? 'text-emerald-300/80' : 'text-emerald-700/80'}`}>
                  {groupeClasseInfo.existe && groupeClasseInfo.membre ? (
                    <>Vous êtes membre · {groupeClasseInfo.nb_membres} participant(s)</>
                  ) : groupeClasseInfo.existe ? (
                    <>{groupeClasseInfo.nb_membres} membre(s) actif(s)</>
                  ) : (
                    <>Aucun groupe encore — soyez le premier</>
                  )}
                </div>
                <div className="mt-1.5">
                  {groupeClasseInfo.existe && groupeClasseInfo.membre ? (
                    <button onClick={handleOuvrirGroupeClasse}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">
                      <MessageSquare size={11} /> Ouvrir le groupe
                    </button>
                  ) : (
                    <button onClick={handleRejoindreClasse} disabled={joiningClasse}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
                      <LogInIcon size={11} />
                      {joiningClasse ? 'Traitement…' : (groupeClasseInfo.existe ? 'Rejoindre le groupe' : 'Créer le groupe de ma classe')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mon toggle visibilité (visible seulement dans onglet En ligne) */}
        {tab === 'en_ligne' && (
          <div className={`px-4 py-2 border-b flex items-center justify-between text-xs ${
            presence?.visible_annuaire === false
              // Bandeau ambre marquant quand on est masqué — pour qu'on
              // s'en rende compte au premier coup d'œil.
              ? (isDark ? 'bg-amber-900/40 border-amber-800' : 'bg-amber-50 border-amber-300')
              : (isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200')
          }`}>
            <span className="flex items-center gap-1.5">
              {presence?.visible_annuaire !== false ? (
                <><Eye size={14} className="text-green-500" /> Vous êtes visible dans l'annuaire</>
              ) : (
                <>
                  <EyeOff size={14} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
                  <span className={`font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                    Vous êtes masqué — les autres ne vous voient pas
                  </span>
                </>
              )}
            </span>
            <button
              onClick={toggleMyVisibility}
              className={`px-2 py-1 rounded text-[11px] font-medium ${
                presence?.visible_annuaire !== false
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              }`}
            >
              {presence?.visible_annuaire !== false ? 'Me masquer' : 'Me rendre visible'}
            </button>
          </div>
        )}

        {/* Recherche + filtre rôle (le filtre rôle uniquement pour En ligne) */}
        <div className={`px-4 py-2 border-b space-y-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === 'en_ligne' ? 'Rechercher un nom…' : 'Rechercher une conversation…'}
              className={`w-full pl-7 pr-2 py-1.5 text-xs rounded border ${
                isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
              }`}
            />
          </div>
          {tab === 'en_ligne' && catsPresentes.length > 1 && (
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setFilterCat('')}
                className={`px-2 py-0.5 text-[11px] rounded border inline-flex items-center gap-1 ${
                  filterCat === ''
                    ? 'bg-blue-600 text-white border-blue-600'
                    : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                Tous
                <span className={`text-[9px] px-1 rounded ${
                  filterCat === '' ? 'bg-white/20' : (isDark ? 'bg-gray-700' : 'bg-gray-100')
                }`}>{presence?.results?.length ?? 0}</span>
              </button>
              {catsPresentes.map(c => (
                <button
                  key={c}
                  onClick={() => setFilterCat(filterCat === c ? '' : c)}
                  className={`px-2 py-0.5 text-[11px] rounded border inline-flex items-center gap-1 ${
                    filterCat === c
                      ? 'bg-blue-600 text-white border-blue-600'
                      : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                  }`}
                >
                  {CATEGORIE_LABELS[c]}
                  <span className={`text-[9px] px-1 rounded ${
                    filterCat === c ? 'bg-white/20' : (isDark ? 'bg-gray-700' : 'bg-gray-100')
                  }`}>{countsParCategorie[c]}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'en_ligne' ? (
            fetchPres && !presence ? (
              <div className="p-4 text-center text-xs text-gray-500">Chargement…</div>
            ) : usersFiltered.length === 0 ? (
              /* Fix 2026-09-02 : quand on est masqué, le backend renvoie
                 results:[] + invisible:true. Expliquer clairement pourquoi
                 la liste est vide plutôt que de laisser croire à un bug. */
              presence?.invisible ? (
                <div className={`m-4 p-4 rounded-lg text-xs ${
                  isDark
                    ? 'bg-amber-900/30 border border-amber-800 text-amber-200'
                    : 'bg-amber-50 border border-amber-300 text-amber-800'
                }`}>
                  <div className="flex items-start gap-2">
                    <EyeOff size={16} className={`flex-shrink-0 mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                    <div>
                      <div className="font-semibold mb-1">Vous êtes masqué(e)</div>
                      <div className="opacity-90 leading-relaxed">
                        Tant que vous restez masqué(e), vous ne voyez pas non
                        plus les autres personnes en ligne (règle de réciprocité).
                        Cliquez sur <strong>« Me rendre visible »</strong> en haut
                        pour retrouver la liste des connectés.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center text-xs text-gray-500">
                  <Wifi size={24} className="mx-auto mb-2 text-gray-400" />
                  {(presence?.results?.length ?? 0) === 0
                    ? 'Personne en ligne pour l\'instant.'
                    : 'Aucun résultat pour ces filtres.'}
                </div>
              )
            ) : (
              /* Groupement par catégorie avec headers de section */
              <div>
                {CATEGORIE_ORDER.filter(c => usersParCategorie[c].length > 0).map(cat => (
                  <section key={cat}>
                    <div className={`px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest ${
                      isDark ? 'bg-gray-800/60 text-gray-400 border-t border-gray-700/70' : 'bg-gray-50 text-gray-500 border-t border-gray-200'
                    }`}>
                      {CATEGORIE_LABELS[cat]}
                      <span className="ml-1.5 normal-case font-normal opacity-70">({usersParCategorie[cat].length})</span>
                    </div>
                    <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {usersParCategorie[cat].map(u => (
                        <li key={u.user_id}>
                          <button
                            disabled={opening}
                            onClick={() => openChat(u)}
                            className={`w-full text-left p-3 hover:${isDark ? 'bg-gray-800' : 'bg-gray-50'} flex items-center gap-3 transition disabled:opacity-50`}
                          >
                            <div className="relative flex-shrink-0">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                                ROLE_COLORS[u.user_role] || 'bg-gray-200 dark:bg-gray-700'
                              }`}>
                                {u.user_nom.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase()}
                              </div>
                              <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-900 ${
                                u.statut === 'online' ? 'bg-green-500'
                                  : u.statut === 'busy' ? 'bg-red-500'
                                  : u.statut === 'meeting' ? 'bg-orange-500'
                                  : u.statut === 'away' ? 'bg-yellow-400'
                                  : 'bg-gray-400'
                              }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{u.user_nom}</div>
                              <div className="text-[11px] text-gray-500 truncate">
                                <span className={`inline-block px-1.5 py-0 rounded text-[10px] mr-1 capitalize ${
                                  ROLE_COLORS[u.user_role] || ''
                                }`}>{u.role_label || u.user_role}</span>
                                {u.activite || (u.statut === 'busy' ? 'Occupé' : u.statut === 'meeting' ? 'En réunion' : '')}
                              </div>
                            </div>
                            <MessageSquare size={14} className="text-gray-400 flex-shrink-0" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )
          ) : (
            // ── Onglet Conversations
            fetchConvs && !convs ? (
              <div className="p-4 text-center text-xs text-gray-500">Chargement…</div>
            ) : convsFiltered.length === 0 ? (
              <div className="p-6 text-center text-xs text-gray-500">
                <Inbox size={24} className="mx-auto mb-2 text-gray-400" />
                {(convs?.results?.length ?? 0) === 0
                  ? 'Aucune conversation. Ouvrez-en une depuis « En ligne ».'
                  : 'Aucun résultat pour cette recherche.'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {convsFiltered.map(c => {
                  const dernier = c.dernier_message;
                  const apercu = dernier
                    ? dernier.type_msg === 'image'
                      ? '📷 Image'
                      : dernier.type_msg === 'fichier'
                        ? `📎 ${dernier.fichier_nom || 'Fichier'}`
                        : (dernier.contenu || '').slice(0, 60)
                    : 'Aucun message';
                  const auteurPrefix = dernier?.a_moi ? 'Vous : ' : '';
                  return (
                    <li key={c.id}>
                      <button
                        onClick={() => openConv(c)}
                        className={`w-full text-left p-3 hover:${isDark ? 'bg-gray-800' : 'bg-gray-50'} flex items-start gap-3 transition ${
                          c.nb_non_lus > 0 ? (isDark ? 'bg-gray-800/50' : 'bg-blue-50/50') : ''
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${
                          c.type_conv === 'broadcast' ? 'bg-red-500' :
                          c.type_conv === 'service' ? 'bg-blue-500' :
                          c.type_conv === 'direct' ? 'bg-slate-500' :
                          'bg-emerald-500'
                        }`}>
                          {c.titre_affichage.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className={`text-sm truncate flex-1 ${c.nb_non_lus > 0 ? 'font-semibold' : 'font-medium'}`}>
                              {c.titre_affichage}
                            </div>
                            {dernier && (
                              <span className="text-[10px] text-gray-500 flex-shrink-0">
                                {fmtRelatif(dernier.date_envoi)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className={`text-[11px] truncate flex-1 ${
                              c.nb_non_lus > 0 ? 'text-gray-200 dark:text-gray-200' : 'text-gray-500'
                            }`}>
                              {auteurPrefix}{apercu}
                            </div>
                            {c.nb_non_lus > 0 && (
                              <span className="min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center bg-red-500 text-white flex-shrink-0">
                                {c.nb_non_lus}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </div>

        <footer className={`px-4 py-2 border-t text-[11px] text-gray-500 flex items-center justify-between ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <span>Actualisation auto {tab === 'en_ligne' ? '30 s' : '15 s'}.</span>
          <button onClick={refresh} className="text-blue-600 hover:underline">Rafraîchir</button>
        </footer>
      </aside>
    </>
  );
};

export default OnlineUsersDrawer;
